import { useCallback, useEffect, useState } from 'react';
import { getAuthOptions, getMe, logout, setUnauthorizedHandler, type AuthOptions, type User } from './api';
import { Login } from './ui/Login';
import { PhoneApp } from './ui/PhoneApp';
import { ResetPassword } from './ui/ResetPassword';
import { AcceptInvite, INVITE_PATH } from './ui/AcceptInvite';

type Auth = { kind: 'checking' } | { kind: 'out'; message?: string; email?: string } | { kind: 'in'; user: User; note?: string };

const RESET_PATH = '/reset';
const onResetPath = () => window.location.pathname === RESET_PATH;
const onInvitePath = () => window.location.pathname === INVITE_PATH;

export function App() {
  const [auth, setAuth] = useState<Auth>({ kind: 'checking' });
  const [options, setOptions] = useState<AuthOptions | null>(null);
  const [reset, setReset] = useState<{ email: string } | null>(() => (onResetPath() ? { email: '' } : null));
  // admin PR 3b: the invitation link (works whether or not this browser is logged in)
  const [invite, setInvite] = useState(onInvitePath);

  useEffect(() => {
    setUnauthorizedHandler(() => setAuth({ kind: 'out', message: 'You were signed out. Log in again to keep using your phone.' }));
    getMe()
      .then((user) => setAuth({ kind: 'in', user }))
      .catch(() => setAuth({ kind: 'out' }));
    getAuthOptions().then(setOptions, () => setOptions(null));
    const onPop = () => {
      setReset(onResetPath() ? { email: '' } : null);
      setInvite(onInvitePath());
    };
    window.addEventListener('popstate', onPop);
    return () => {
      setUnauthorizedHandler(null);
      window.removeEventListener('popstate', onPop);
    };
  }, []);

  const onLogout = useCallback(async () => {
    try {
      await logout();
    } catch {
      /* the cookie is cleared server-side anyway when the session is gone */
    }
    setAuth({ kind: 'out' });
  }, []);

  // time-zone PR: a fresh login didn't call /v1/me, so tell the server this browser's
  // zone now (admins see it for "Automatic" users); nothing waits for it
  const loggedIn = (user: User, note?: string): Auth => {
    void getMe().catch(() => undefined);
    return { kind: 'in', user, ...(note ? { note } : {}) };
  };

  const onSessionEnded = useCallback(() => {
    setAuth({ kind: 'out', message: 'Your session ended (signed out elsewhere or the account changed). Log in again.' });
  }, []);

  const openReset = (email: string) => {
    history.pushState(null, '', RESET_PATH);
    setReset({ email });
  };
  const leaveReset = (next: Auth) => {
    if (onResetPath()) history.replaceState(null, '', '/');
    setReset(null);
    setAuth(next);
  };

  const leaveInvite = (next: Auth) => {
    if (onInvitePath()) history.replaceState(null, '', '/');
    setInvite(false);
    setAuth(next);
  };

  // The invitation page: choosing the password logs this browser in as the invited person.
  if (invite) {
    return (
      <AcceptInvite
        options={options}
        onLoggedIn={(user) => leaveInvite(loggedIn(user))}
        onLeave={(message, email) => leaveInvite({ kind: 'out', ...(message ? { message } : {}), ...(email ? { email } : {}) })}
      />
    );
  }

  // The reset page works whether or not this browser is logged in (finishing it signs out everywhere).
  if (reset) {
    return (
      <ResetPassword
        options={options}
        initialEmail={reset.email}
        onDone={(email) => leaveReset({ kind: 'out', email, message: 'Your password was changed. Log in with your new password.' })}
        onBack={(email) => leaveReset(auth.kind === 'in' ? auth : { kind: 'out', email })}
      />
    );
  }
  if (auth.kind === 'checking') {
    return (
      <main className="boot" aria-busy="true">
        <span className="boot-mark" aria-hidden />
        <span className="sr-only">Loading CloudPhone</span>
      </main>
    );
  }
  if (auth.kind === 'out') {
    return (
      <Login
        key={auth.email ?? ''}
        notice={auth.message}
        initialEmail={auth.email}
        options={options}
        onLoggedIn={(user, note) => setAuth(loggedIn(user, note))}
        onForgot={openReset}
      />
    );
  }
  return <PhoneApp key={auth.user.id} user={auth.user} loginNote={auth.note} onLogout={onLogout} onSessionEnded={onSessionEnded} />;
}
